import siteMap from '../../../siteMap'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { useHistory } from 'react-router-dom'
import { Alert, Button, Chip, Grid, Stack, Typography, useTheme } from '@mui/material'
import { useLocations } from '../../../hooks/locations'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import { EmissionTier, MetricState, PERIODS, CanonicalPeriod } from '../common'
import { normalizeWaterRiskScore, WaterRiskScoreBadge } from '../common/waterRisk/utils'
import { LoadingValue } from './PortfolioLoading'
import PortfolioSummaryMetricCard from './PortfolioSummaryMetricCard'
import PortfolioPropertyBreakdownTable, { type PortfolioMetricSpec } from './PortfolioPropertyBreakdownTable'
import PortfolioPropertyMetricMap, {
  type PortfolioMapColorOption,
  type PortfolioMapRadiusOption,
  type NumericMetricSpec,
  type PopupFieldSpec,
} from './PortfolioPropertyMetricMap'
import { renderAggregateTooltip, renderHelpLines } from '../common/aggregateHelpText'
import { formatPropertyCoverage, meanAggregateValues, type AggregateSummary } from '../common/portfolioAggregates'

type PropertySubset = { mode: 'all' } | { mode: 'selected'; ids: number[] }

type PeriodicMetricMap = {
  values: Record<number, Partial<Record<CanonicalPeriod, number | null>>>
  status: Record<number, MetricState | null>
}

function formatNumber(value: number, digits: number = 1) {
  return value.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  })
}

function parseWaterStressScore(entry: any, tierKey: string, period: CanonicalPeriod): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  const tierData = entry.data?.data?.[tierKey]
  const node = tierData?.[period] ?? tierData?.score?.[period] ?? null
  const raw =
    typeof node?.y !== 'undefined'
      ? node.y
      : typeof node === 'number' || typeof node === 'string'
      ? node
      : typeof node?.value !== 'undefined'
      ? node.value
      : null
  const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number.parseFloat(raw) : null
  return typeof value === 'number' && !Number.isNaN(value) ? value : null
}

function parsePoint(entry: any, tierKey: string, period: CanonicalPeriod): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  const node = entry.data?.data?.[tierKey]?.[period]
  const raw =
    typeof node?.y === 'number'
      ? node.y
      : typeof node?.y === 'string'
      ? Number.parseFloat(node.y)
      : typeof node === 'number'
      ? node
      : typeof node === 'string'
      ? Number.parseFloat(node)
      : null
  return typeof raw === 'number' && !Number.isNaN(raw) ? raw : null
}

const waterRiskScorePalette = {
  1: '#c6e8be',
  2: '#f7e4af',
  3: '#f6c8a4',
  4: '#f3a5a5',
  5: '#e8747d',
}

function PortfolioWaterRisk({
  emissionTier,
  selectedPeriod,
}: {
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ group_id: groupId ?? undefined })
  const numberOfProperties = locations?.length ?? null
  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'wr', emissionTier)),
    [emissionTier, history],
  )

  const { data: waterStressByProperty, isLoading: waterStressLoading } = usePortfolioClimateData('wr:ws', {
    groupId: groupId ?? undefined,
  })
  const { data: waterDemandByProperty, isLoading: waterDemandLoading } = usePortfolioClimateData('wr:ww', {
    groupId: groupId ?? undefined,
  })
  const { data: blueWaterByProperty, isLoading: blueWaterLoading } = usePortfolioClimateData('wr:ba', {
    groupId: groupId ?? undefined,
  })
  const { data: depletionByProperty, isLoading: depletionLoading } = usePortfolioClimateData('wr:wd', {
    groupId: groupId ?? undefined,
  })
  const { data: seasonalVariabilityByProperty, isLoading: seasonalVariabilityLoading } = usePortfolioClimateData(
    'wr:sv',
    {
      groupId: groupId ?? undefined,
    },
  )
  const { data: interannualVariabilityByProperty, isLoading: interannualVariabilityLoading } = usePortfolioClimateData(
    'wr:iv',
    {
      groupId: groupId ?? undefined,
    },
  )

  const [propertySubset, setPropertySubset] = useState<PropertySubset>({ mode: 'all' })
  const [mapClearSelectionToken, setMapClearSelectionToken] = useState(0)

  useEffect(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(v => v + 1)
  }, [groupId])

  const activePropertyIds = useMemo(() => {
    return propertySubset.mode === 'selected' ? propertySubset.ids : undefined
  }, [propertySubset])

  const handleConfirmSelection = useCallback((propertyIds: number[]) => {
    if (!propertyIds.length) {
      setPropertySubset({ mode: 'all' })
      return
    }
    setPropertySubset({ mode: 'selected', ids: propertyIds })
  }, [])

  const handleClearSelection = useCallback(() => {
    setPropertySubset({ mode: 'all' })
  }, [])

  const clearActiveSubset = useCallback(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(v => v + 1)
  }, [])

  const tierKey = emissionTier as unknown as string

  const waterStressMetric = useMemo<PeriodicMetricMap>(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (waterStressByProperty && typeof waterStressByProperty === 'object') {
      Object.values(waterStressByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(period => (per[period] = parseWaterStressScore(entry, tierKey, period)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [tierKey, waterStressByProperty])

  const waterDemandMetric = useMemo<PeriodicMetricMap>(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (waterDemandByProperty && typeof waterDemandByProperty === 'object') {
      Object.values(waterDemandByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(period => (per[period] = parsePoint(entry, tierKey, period)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [tierKey, waterDemandByProperty])

  const blueWaterMetric = useMemo<PeriodicMetricMap>(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (blueWaterByProperty && typeof blueWaterByProperty === 'object') {
      Object.values(blueWaterByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(period => (per[period] = parsePoint(entry, tierKey, period)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [blueWaterByProperty, tierKey])

  const depletionMetric = useMemo<PeriodicMetricMap>(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (depletionByProperty && typeof depletionByProperty === 'object') {
      Object.values(depletionByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(period => (per[period] = parsePoint(entry, tierKey, period)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [depletionByProperty, tierKey])

  const seasonalVariabilityMetric = useMemo<PeriodicMetricMap>(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (seasonalVariabilityByProperty && typeof seasonalVariabilityByProperty === 'object') {
      Object.values(seasonalVariabilityByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(period => (per[period] = parsePoint(entry, tierKey, period)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [seasonalVariabilityByProperty, tierKey])

  const interannualVariabilityMetric = useMemo<PeriodicMetricMap>(() => {
    const values: Record<number, Partial<Record<CanonicalPeriod, number | null>>> = {}
    const status: Record<number, MetricState | null> = {}
    if (interannualVariabilityByProperty && typeof interannualVariabilityByProperty === 'object') {
      Object.values(interannualVariabilityByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        const per: Partial<Record<CanonicalPeriod, number | null>> = {}
        PERIODS.forEach(period => (per[period] = parsePoint(entry, tierKey, period)))
        values[propertyId] = per
      })
    }
    return { values, status }
  }, [interannualVariabilityByProperty, tierKey])

  const kpis = useMemo(() => {
    type Agg = {
      avgScore: number | null
      highCount: number
      total: number
      avgDemand: number | null
      avgSupply: number | null
    }
    const out: Record<CanonicalPeriod, Agg> = {
      [CanonicalPeriod.SHORT_TERM]: { avgScore: null, highCount: 0, total: 0, avgDemand: null, avgSupply: null },
      [CanonicalPeriod.MID_TERM]: { avgScore: null, highCount: 0, total: 0, avgDemand: null, avgSupply: null },
      [CanonicalPeriod.LONG_TERM]: { avgScore: null, highCount: 0, total: 0, avgDemand: null, avgSupply: null },
    }

    PERIODS.forEach(period => {
      const collect = (metricValues: Record<number, Partial<Record<CanonicalPeriod, number | null>>>) => {
        if (activePropertyIds) {
          return activePropertyIds
            .map(id => metricValues[id]?.[period] ?? null)
            .filter((v): v is number => typeof v === 'number')
        }
        return Object.values(metricValues)
          .map(v => v[period])
          .filter((v): v is number => typeof v === 'number')
      }

      const scoreValues = collect(waterStressMetric.values)
      const demandValues = collect(waterDemandMetric.values)
      const supplyValues = collect(blueWaterMetric.values)
      const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null)
      const normalizedScores = scoreValues
        .map(score => normalizeWaterRiskScore(score))
        .filter((score): score is number => typeof score === 'number')

      out[period] = {
        avgScore: mean(scoreValues),
        highCount: normalizedScores.filter(score => score >= 4).length,
        total: scoreValues.length,
        avgDemand: mean(demandValues),
        avgSupply: mean(supplyValues),
      }
    })

    return out
  }, [activePropertyIds, blueWaterMetric.values, waterDemandMetric.values, waterStressMetric.values])

  const selectedPropertyIds = useMemo(() => {
    if (activePropertyIds) return activePropertyIds
    return (locations ?? []).map((location: any) => location.id)
  }, [activePropertyIds, locations])

  const selectedSummaries = useMemo(
    (): {
      scoreSummary: AggregateSummary
      demandSummary: AggregateSummary
      supplySummary: AggregateSummary
    } => ({
      scoreSummary: meanAggregateValues(
        selectedPropertyIds,
        propertyId => waterStressMetric.values[propertyId]?.[selectedPeriod] ?? null,
      ),
      demandSummary: meanAggregateValues(
        selectedPropertyIds,
        propertyId => waterDemandMetric.values[propertyId]?.[selectedPeriod] ?? null,
      ),
      supplySummary: meanAggregateValues(
        selectedPropertyIds,
        propertyId => blueWaterMetric.values[propertyId]?.[selectedPeriod] ?? null,
      ),
    }),
    [blueWaterMetric.values, selectedPeriod, selectedPropertyIds, waterDemandMetric.values, waterStressMetric.values],
  )

  const tableMetrics = useMemo<PortfolioMetricSpec[]>(
    () => [
      {
        kind: 'score',
        key: 'score',
        label: 'Risk Rating',
        perPeriod: true,
        width: 150,
        loading: waterStressLoading,
        getValue: (propertyId, period) =>
          waterStressMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        renderScore: value => <WaterRiskScoreBadge score={value} size="dense" />,
        status: {
          key: 'statusWS',
          label: 'Status (Risk Rating)',
          hiddenByDefault: true,
          loading: waterStressLoading,
          getValue: propertyId => waterStressMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'water_demand',
        label: 'Gross Water Demand',
        perPeriod: true,
        width: 180,
        loading: waterDemandLoading,
        format: 'fixed1',
        suffix: 'cm/year',
        getValue: (propertyId, period) =>
          waterDemandMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusDemand',
          label: 'Status (Gross Water Demand)',
          hiddenByDefault: true,
          loading: waterDemandLoading,
          getValue: propertyId => waterDemandMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'blue_water',
        label: 'Available Blue Water',
        perPeriod: true,
        width: 180,
        loading: blueWaterLoading,
        format: 'fixed1',
        suffix: 'cm/year',
        getValue: (propertyId, period) =>
          blueWaterMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusSupply',
          label: 'Status (Available Blue Water)',
          hiddenByDefault: true,
          loading: blueWaterLoading,
          getValue: propertyId => blueWaterMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'baseline_water_depletion',
        label: 'Baseline Water Depletion',
        perPeriod: true,
        width: 190,
        loading: depletionLoading,
        format: 'fixed1',
        getValue: (propertyId, period) =>
          depletionMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusDepletion',
          label: 'Status (Baseline Water Depletion)',
          hiddenByDefault: true,
          loading: depletionLoading,
          getValue: propertyId => depletionMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'seasonal_variability',
        label: 'Seasonal Variability',
        perPeriod: true,
        width: 180,
        loading: seasonalVariabilityLoading,
        format: 'fixed1',
        getValue: (propertyId, period) =>
          seasonalVariabilityMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusSeasonalVariability',
          label: 'Status (Seasonal Variability)',
          hiddenByDefault: true,
          loading: seasonalVariabilityLoading,
          getValue: propertyId => seasonalVariabilityMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'interannual_variability',
        label: 'Interannual Variability',
        perPeriod: true,
        width: 190,
        loading: interannualVariabilityLoading,
        format: 'fixed1',
        getValue: (propertyId, period) =>
          interannualVariabilityMetric.values[propertyId]?.[period ?? CanonicalPeriod.LONG_TERM] ?? null,
        status: {
          key: 'statusInterannualVariability',
          label: 'Status (Interannual Variability)',
          hiddenByDefault: true,
          loading: interannualVariabilityLoading,
          getValue: propertyId => interannualVariabilityMetric.status[propertyId] ?? null,
        },
      },
    ],
    [
      blueWaterLoading,
      blueWaterMetric.status,
      blueWaterMetric.values,
      depletionLoading,
      depletionMetric.status,
      depletionMetric.values,
      interannualVariabilityLoading,
      interannualVariabilityMetric.status,
      interannualVariabilityMetric.values,
      seasonalVariabilityLoading,
      seasonalVariabilityMetric.status,
      seasonalVariabilityMetric.values,
      waterDemandLoading,
      waterDemandMetric.status,
      waterDemandMetric.values,
      waterStressLoading,
      waterStressMetric.status,
      waterStressMetric.values,
    ],
  )

  const waterStressNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'score',
      label: 'Risk Rating',
      perPeriod: true,
      loading: waterStressLoading,
      getValue: (propertyId, period) => waterStressMetric.values[propertyId]?.[period] ?? null,
      renderValue: value => <WaterRiskScoreBadge score={value} size="dense" />,
    }),
    [waterStressLoading, waterStressMetric.values],
  )

  const waterDemandNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'water_demand',
      label: 'Gross Water Demand',
      perPeriod: true,
      loading: waterDemandLoading,
      decimals: 2,
      suffix: 'cm/year',
      getValue: (propertyId, period) => waterDemandMetric.values[propertyId]?.[period] ?? null,
    }),
    [waterDemandLoading, waterDemandMetric.values],
  )

  const blueWaterNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'blue_water',
      label: 'Available Blue Water',
      perPeriod: true,
      loading: blueWaterLoading,
      decimals: 2,
      suffix: 'cm/year',
      getValue: (propertyId, period) => blueWaterMetric.values[propertyId]?.[period] ?? null,
    }),
    [blueWaterLoading, blueWaterMetric.values],
  )

  const mapColorOptions = useMemo<PortfolioMapColorOption[]>(
    () => [
      {
        key: 'score',
        label: 'Risk Rating',
        type: 'discrete',
        metric: waterStressNumericMetric,
        palette: waterRiskScorePalette,
        labels: {
          1: 'Low',
          2: 'Low-Medium',
          3: 'Medium-High',
          4: 'High',
          5: 'Extremely High',
        },
        normalize: value => normalizeWaterRiskScore(value) ?? 1,
      },
      {
        key: 'water_demand',
        label: 'Gross Water Demand',
        type: 'continuous',
        metric: waterDemandNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
      {
        key: 'blue_water',
        label: 'Available Blue Water',
        type: 'continuous',
        metric: blueWaterNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#e74c3c' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#2ecc71' },
          ],
        },
      },
    ],
    [blueWaterNumericMetric, waterDemandNumericMetric, waterStressNumericMetric],
  )

  const mapRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(
    () => [
      {
        key: 'water_demand',
        label: 'Gross Water Demand',
        type: 'scaled',
        metric: waterDemandNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
      {
        key: 'blue_water',
        label: 'Available Blue Water',
        type: 'scaled',
        metric: blueWaterNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
    ],
    [blueWaterNumericMetric, waterDemandNumericMetric],
  )

  const mapPopupFields = useMemo<PopupFieldSpec[]>(
    () => [
      {
        key: 'blue_water',
        label: 'Available Blue Water',
        perPeriod: true,
        loading: blueWaterLoading,
        decimals: 2,
        suffix: 'cm/year',
        getValue: (propertyId, period) => blueWaterMetric.values[propertyId]?.[period] ?? null,
      },
    ],
    [blueWaterLoading, blueWaterMetric.values],
  )

  const selectedKpi = kpis[selectedPeriod]
  const selectedHighPct = selectedKpi.total ? Math.round((selectedKpi.highCount / selectedKpi.total) * 100) : null
  const riskRatingHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        selectedSummaries.scoreSummary,
        'Average Risk Rating',
        'Average across available water stress ratings',
      ),
    [selectedSummaries.scoreSummary],
  )
  const highRiskHelpText = useMemo(
    () =>
      renderHelpLines([
        'Count of selected properties rated High or Very High.',
        `Score coverage: ${formatPropertyCoverage(
          selectedSummaries.scoreSummary.contributors,
          selectedSummaries.scoreSummary.total,
        )}.`,
        selectedHighPct != null
          ? `Among available scores: ${selectedHighPct}% are High / Very High.`
          : 'Among available scores: —.',
      ]),
    [selectedHighPct, selectedKpi.highCount, selectedSummaries.scoreSummary],
  )
  const waterDemandHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        selectedSummaries.demandSummary,
        'Average Gross Water Demand',
        'Average across available gross-water-demand values',
      ),
    [selectedSummaries.demandSummary],
  )
  const blueWaterHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        selectedSummaries.supplySummary,
        'Average Available Blue Water',
        'Average across available blue-water values',
      ),
    [selectedSummaries.supplySummary],
  )
  const kpiMotionKey = `${emissionTier}-${selectedPeriod}`

  return (
    <Stack alignItems={'center'} pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.['lg'], width: '100%' }} spacing={2}>
        {activePropertyIds && (
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearActiveSubset}>
                Clear selection
              </Button>
            }>
            <Typography variant="body2" fontWeight={800}>
              Property filter
            </Typography>
            <Typography variant="body2">
              Showing data for a subset of properties ({activePropertyIds.length.toLocaleString()} out of{' '}
              {numberOfProperties?.toLocaleString() ?? '—'}).
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard
              title="Average Risk Rating"
              helpText={riskRatingHelpText}
              loading={waterStressLoading}
              transitionKey={kpiMotionKey}>
              <LoadingValue loading={waterStressLoading} variant="rounded" width={72} height={20}>
                <WaterRiskScoreBadge score={selectedKpi.avgScore} size="dense" />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard
              title="High / Very High Risk Ratings"
              helpText={highRiskHelpText}
              loading={waterStressLoading}
              transitionKey={kpiMotionKey}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LoadingValue loading={waterStressLoading} variant="rounded" width={60} height={24}>
                  <Chip label={selectedKpi.highCount} />
                </LoadingValue>
              </Stack>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard
              title="Average Gross Water Demand"
              helpText={waterDemandHelpText}
              loading={waterDemandLoading}
              transitionKey={kpiMotionKey}>
              <LoadingValue loading={waterDemandLoading} variant="rounded" width={140} height={24}>
                <Chip
                  label={selectedKpi.avgDemand != null ? `${formatNumber(selectedKpi.avgDemand!, 1)} cm/year` : '—'}
                />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <PortfolioSummaryMetricCard
              title="Average Available Blue Water"
              helpText={blueWaterHelpText}
              loading={blueWaterLoading}
              transitionKey={kpiMotionKey}>
              <LoadingValue loading={blueWaterLoading} variant="rounded" width={140} height={24}>
                <Chip
                  label={selectedKpi.avgSupply != null ? `${formatNumber(selectedKpi.avgSupply!, 1)} cm/year` : '—'}
                />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>
        </Grid>

        <PortfolioPropertyMetricMap
          title="Portfolio Map"
          height={600}
          selectedPeriod={selectedPeriod}
          colorOptions={mapColorOptions}
          radiusOptions={mapRadiusOptions}
          defaultColorKey="score"
          defaultRadiusKey="default"
          popupFields={mapPopupFields}
          onViewProperty={goToProperty}
          onConfirmSelection={handleConfirmSelection}
          onClearSelection={handleClearSelection}
          clearSelectionToken={mapClearSelectionToken}
        />

        <PortfolioPropertyBreakdownTable
          metrics={tableMetrics}
          selectedPeriod={selectedPeriod}
          filterPropertyIds={activePropertyIds}
          onViewProperty={goToProperty}
          height={680}
          initialSort={{ metricKey: 'score', period: selectedPeriod, sort: 'desc' }}
        />
      </Stack>
    </Stack>
  )
}

export default PortfolioWaterRisk
